import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export interface LeadModalContextValue {
  open: boolean;
  serviceSlug?: string;
  openModal: (slug?: string) => void;
  closeModal: () => void;
}

const LeadModalContext = createContext<LeadModalContextValue | undefined>(undefined);

export interface LeadModalProviderProps {
  children: ReactNode;
}

export function LeadModalProvider({ children }: LeadModalProviderProps) {
  const [open, setOpen] = useState(false);
  const [serviceSlug, setServiceSlug] = useState<string | undefined>(undefined);

  const openModal = useCallback((slug?: string) => {
    setServiceSlug(slug);
    setOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);

  const value = useMemo<LeadModalContextValue>(
    () => ({ open, serviceSlug, openModal, closeModal }),
    [open, serviceSlug, openModal, closeModal],
  );

  return <LeadModalContext.Provider value={value}>{children}</LeadModalContext.Provider>;
}

export function useLeadModal(): LeadModalContextValue {
  const ctx = useContext(LeadModalContext);
  if (!ctx) {
    throw new Error('useLeadModal must be used within LeadModalProvider');
  }
  return ctx;
}

export default LeadModalContext;
